/**
 * Upgrade Prompt Component
 * Contextual upgrade prompts with tier comparison and benefits
 */

import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Progress } from './ui/progress'
import { Zap, Crown, Infinity, CheckCircle, XCircle, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useBusinessAnalytics } from '../utils/analytics'
import { MARKETING_PRICING_URL } from '../constants/urls'

export type UpgradeContext = 
  | 'memory_limit'
  | 'space_limit'
  | 'api_rate_limit'
  | 'feature_access'
  | 'memory_expiration'
  | 'general'

export interface UpgradePromptProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade: () => void
  context: UpgradeContext
  currentTier: string
  nextTier: string
  usageInfo?: {
    current: number
    limit: number
    percentage: number
    warningLevel: 'info' | 'warning' | 'critical' | 'exceeded'
  }
  benefits?: {
    new_features: string[]
    improved_limits: Record<string, { current: number | string, new: number | string }>
  }
  customMessage?: string
  showComparison?: boolean
}

export function UpgradePrompt({
  isOpen,
  onClose,
  onUpgrade,
  context,
  currentTier,
  nextTier,
  usageInfo,
  benefits,
  customMessage,
  showComparison = true
}: UpgradePromptProps) {
  const { t } = useTranslation();
  const { trackUpgradeClick, trackUpgradeModalView } = useBusinessAnalytics();

  // Track modal view when opened
  useEffect(() => {
    if (isOpen) {
      trackUpgradeModalView({
        source: 'upgrade_prompt',
        current_tier: currentTier,
        upgrade_context: context,
        trigger_reason: customMessage || 'user_action'
      });
    }
  }, [isOpen, currentTier, context, customMessage, trackUpgradeModalView]);

  const TIER_CONFIGS = {
    free: {
      name: t('tiers.free.name', { ns: 'pricing' }) as string,
      icon: '✨',
      color: 'bg-slate-100 text-slate-700',
      description: t('tiers.free.description', { ns: 'pricing' }) as string
    },
    pro: {
      name: t('tiers.pro.name', { ns: 'pricing' }) as string,
      icon: '⚡',
      color: 'bg-blue-100 text-blue-700',
      description: t('tiers.pro.description', { ns: 'pricing' }) as string
    },
    team: {
      name: t('tiers.pro.name', { ns: 'pricing' }) as string,
      icon: '⚡',
      color: 'bg-purple-100 text-purple-700',
      description: t('tiers.pro.description', { ns: 'pricing' }) as string
    },
    enterprise: {
      name: t('tiers.enterprise.name', { ns: 'pricing' }) as string,
      icon: '🏢',
      color: 'bg-green-100 text-green-700',
      description: t('tiers.enterprise.description', { ns: 'pricing' }) as string
    }
  }

  const CONTEXT_MESSAGES = {
    memory_limit: {
      title: t('contexts.memoryLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.memoryLimit.description', { ns: 'pricing' }) as string,
      icon: <Infinity className="h-6 w-6 text-blue-500" />
    },
    space_limit: {
      title: t('contexts.spaceLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.spaceLimit.description', { ns: 'pricing' }) as string,
      icon: <Crown className="h-6 w-6 text-purple-500" />
    },
    api_rate_limit: {
      title: t('contexts.apiRateLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.apiRateLimit.description', { ns: 'pricing' }) as string,
      icon: <TrendingUp className="h-6 w-6 text-orange-500" />
    },
    feature_access: {
      title: t('contexts.featureAccess.title', { ns: 'pricing' }) as string,
      description: t('contexts.featureAccess.description', { ns: 'pricing' }) as string,
      icon: <Zap className="h-6 w-6 text-yellow-500" />
    },
    memory_expiration: {
      title: t('contexts.memoryExpiration.title', { ns: 'pricing' }) as string,
      description: t('contexts.memoryExpiration.description', { ns: 'pricing' }) as string,
      icon: <CheckCircle className="h-6 w-6 text-green-500" />
    },
    general: {
      title: t('contexts.general.title', { ns: 'pricing' }) as string,
      description: t('contexts.general.description', { ns: 'pricing' }) as string,
      icon: <Zap className="h-6 w-6 text-blue-500" />
    }
  }

  const contextConfig = CONTEXT_MESSAGES[context]
  const currentTierConfig = TIER_CONFIGS[currentTier as keyof typeof TIER_CONFIGS]
  const nextTierConfig = TIER_CONFIGS[nextTier as keyof typeof TIER_CONFIGS]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {contextConfig.icon}
            <div>
              <DialogTitle>{contextConfig.title}</DialogTitle>
              <DialogDescription>
                {customMessage || contextConfig.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Usage Information */}
          {usageInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('usage.currentUsage', { ns: 'pricing' }) as string}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('usage.used', { ns: 'pricing' }) as string}: {usageInfo.current}</span>
                    <span>{t('usage.limit', { ns: 'pricing' }) as string}: {usageInfo.limit}</span>
                  </div>
                  <Progress 
                    value={Math.min(usageInfo.percentage, 100)} 
                    className="h-2"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      {usageInfo.percentage.toFixed(1)}% {t('usage.used', { ns: 'pricing' }) as string}
                    </span>
                    <Badge variant={usageInfo.warningLevel === 'exceeded' ? 'destructive' : 'secondary'}>
                      {usageInfo.warningLevel === 'exceeded' ? (t('usage.limitExceeded', { ns: 'pricing' }) as string) : (t('usage.inUse', { ns: 'pricing' }) as string)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tier Comparison */}
          {showComparison && (
            <div className="grid grid-cols-2 gap-4">
              {/* Current Tier */}
              <Card className="relative">
                <div className="absolute top-2 right-2">
                  <Badge variant="outline">{t('comparison.current', { ns: 'pricing' }) as string}</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>{currentTierConfig?.icon}</span>
                    {currentTierConfig?.name}
                  </CardTitle>
                  <CardDescription>
                    {currentTierConfig?.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    {t('comparison.currentPlanLimitations', { ns: 'pricing' }) as string}
                  </div>
                </CardContent>
              </Card>

              {/* Next Tier */}
              <Card className="relative border-blue-200 bg-blue-50/50">
                <div className="absolute top-2 right-2">
                  <Badge className="bg-blue-500">{t('comparison.recommended', { ns: 'pricing' }) as string}</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>{nextTierConfig?.icon}</span>
                    {nextTierConfig?.name}
                  </CardTitle>
                  <CardDescription>
                    {nextTierConfig?.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-blue-600 font-medium">
                    {t('comparison.unlockMoreCapabilities', { ns: 'pricing' }) as string}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Benefits */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('benefits.whatYoullGet', { ns: 'pricing' }) as string}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show backend benefits if available, otherwise show default benefits */}
              {benefits && benefits.new_features.length > 0 ? (
                <div>
                  <h4 className="font-medium text-sm mb-2">{t('benefits.newFeatures', { ns: 'pricing' }) as string}</h4>
                  <ul className="space-y-1">
                    {benefits.new_features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <h4 className="font-medium text-sm mb-2">{t('benefits.proFeatures', { ns: 'pricing' }) as string}</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.unlimitedMemories.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.unlimitedMemories.description', { ns: 'pricing' }) as string}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.memorySpaces.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.memorySpaces.description', { ns: 'pricing' }) as string}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.apiCalls.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.apiCalls.description', { ns: 'pricing' }) as string}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.advancedSearch.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.advancedSearch.description', { ns: 'pricing' }) as string}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.webhookAutomation.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.webhookAutomation.description', { ns: 'pricing' }) as string}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                      <span><strong>{t('benefits.features.neverExpiring.title', { ns: 'pricing' }) as string}</strong> - {t('benefits.features.neverExpiring.description', { ns: 'pricing' }) as string}</span>
                    </li>
                  </ul>
                </div>
              )}

              {/* Improved Limits - show if available */}
              {benefits && Object.keys(benefits.improved_limits).length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">{t('benefits.limitImprovements', { ns: 'pricing' }) as string}</h4>
                  <div className="space-y-2">
                    {Object.entries(benefits.improved_limits).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-sm">
                        <span className="capitalize">{key.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{value.current}</span>
                          <span>→</span>
                          <span className="font-medium text-green-600">{value.new}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Display */}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('pricing.monthlyPricing', { ns: 'pricing' }) as string}</span>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">{t('pricing.price', { ns: 'pricing' }) as string}</div>
                    <div className="text-xs text-muted-foreground">{t('pricing.cancelAnytime', { ns: 'pricing' }) as string}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('actions.maybeLater', { ns: 'pricing' }) as string}
          </Button>
          <Button 
            onClick={() => {
              try {
                // Track the upgrade click
                trackUpgradeClick({
                  source: 'upgrade_prompt',
                  current_tier: currentTier,
                  target_tier: nextTier,
                  upgrade_context: context,
                  element_type: 'modal',
                  trigger_reason: customMessage || 'user_action',
                  additional_context: {
                    usage_info: usageInfo,
                    has_benefits: !!benefits,
                    show_comparison: showComparison
                  }
                });
                
                onUpgrade();
              } catch (error) {
                console.error('Upgrade action failed:', error);
                // Fallback to external pricing page
                window.open(MARKETING_PRICING_URL, '_blank');
                onClose();
              }
            }} 
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Zap className="h-4 w-4 mr-2" />
            {t('upgrade.actions.upgradeTo', { tier: nextTierConfig?.name, ns: 'pricing' }) as string}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Convenience component for inline upgrade prompts
export function InlineUpgradePrompt({
  context,
  currentTier,
  nextTier,
  onUpgrade,
  className = ""
}: {
  context: UpgradeContext
  currentTier: string
  nextTier: string
  onUpgrade: () => void
  className?: string
}) {
  const { t } = useTranslation();
  const { trackUpgradeClick } = useBusinessAnalytics();

  const CONTEXT_MESSAGES = {
    memory_limit: {
      title: t('contexts.memoryLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.memoryLimit.description', { ns: 'pricing' }) as string,
      icon: <Infinity className="h-6 w-6 text-blue-500" />
    },
    space_limit: {
      title: t('contexts.spaceLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.spaceLimit.description', { ns: 'pricing' }) as string,
      icon: <Crown className="h-6 w-6 text-purple-500" />
    },
    api_rate_limit: {
      title: t('contexts.apiRateLimit.title', { ns: 'pricing' }) as string,
      description: t('contexts.apiRateLimit.description', { ns: 'pricing' }) as string,
      icon: <TrendingUp className="h-6 w-6 text-orange-500" />
    },
    feature_access: {
      title: t('contexts.featureAccess.title', { ns: 'pricing' }) as string,
      description: t('contexts.featureAccess.description', { ns: 'pricing' }) as string,
      icon: <Zap className="h-6 w-6 text-yellow-500" />
    },
    memory_expiration: {
      title: t('contexts.memoryExpiration.title', { ns: 'pricing' }) as string,
      description: t('contexts.memoryExpiration.description', { ns: 'pricing' }) as string,
      icon: <CheckCircle className="h-6 w-6 text-green-500" />
    },
    general: {
      title: t('contexts.general.title', { ns: 'pricing' }) as string,
      description: t('contexts.general.description', { ns: 'pricing' }) as string,
      icon: <Zap className="h-6 w-6 text-blue-500" />
    }
  }

  const TIER_CONFIGS = {
    free: {
      name: t('tiers.free.name', { ns: 'pricing' }) as string,
      icon: '✨',
      color: 'bg-slate-100 text-slate-700',
      description: t('tiers.free.description', { ns: 'pricing' }) as string
    },
    pro: {
      name: t('tiers.pro.name', { ns: 'pricing' }) as string,
      icon: '⚡',
      color: 'bg-blue-100 text-blue-700',
      description: t('tiers.pro.description', { ns: 'pricing' }) as string
    },
    team: {
      name: t('tiers.pro.name', { ns: 'pricing' }) as string,
      icon: '⚡',
      color: 'bg-purple-100 text-purple-700',
      description: t('tiers.pro.description', { ns: 'pricing' }) as string
    },
    enterprise: {
      name: t('tiers.enterprise.name', { ns: 'pricing' }) as string,
      icon: '🏢',
      color: 'bg-green-100 text-green-700',
      description: t('tiers.enterprise.description', { ns: 'pricing' }) as string
    }
  }

  const contextConfig = CONTEXT_MESSAGES[context]
  const nextTierConfig = TIER_CONFIGS[nextTier as keyof typeof TIER_CONFIGS]

  return (
    <Card className={`border-blue-200 bg-blue-50/50 ${className}`}>
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          {contextConfig.icon}
          <div className="flex-1">
            <h4 className="font-medium text-sm">{contextConfig.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">
              {contextConfig.description}
            </p>
            <Button 
              size="sm" 
              onClick={() => {
                try {
                  // Track the inline upgrade click
                  trackUpgradeClick({
                    source: 'inline_prompt',
                    current_tier: currentTier,
                    target_tier: nextTier,
                    upgrade_context: context,
                    element_type: 'card',
                    trigger_reason: 'inline_prompt_action'
                  });
                  
                  onUpgrade();
                } catch (error) {
                  console.error('Upgrade action failed:', error);
                  // Fallback to external pricing page
                  window.open(MARKETING_PRICING_URL, '_blank');
                }
              }}
              className="mt-3 bg-blue-600 hover:bg-blue-700"
            >
              <Zap className="h-3 w-3 mr-1" />
              {t('upgrade.actions.upgradeTo', { tier: nextTierConfig?.name, ns: 'pricing' }) as string}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Usage limit warning component
export function UsageLimitWarning({
  resourceType,
  current,
  limit,
  warningLevel,
  onUpgrade,
  className = ""
}: {
  resourceType: string
  current: number
  limit: number
  warningLevel: 'info' | 'warning' | 'critical' | 'exceeded'
  onUpgrade: () => void
  className?: string
}) {
  const { t } = useTranslation();
  const { trackUpgradeClick } = useBusinessAnalytics();
  
  const percentage = (current / limit) * 100
  const remaining = Math.max(0, limit - current)

  const getVariant = () => {
    switch (warningLevel) {
      case 'exceeded': return 'destructive'
      case 'critical': return 'destructive'
      case 'warning': return 'default'
      default: return 'secondary'
    }
  }

  const getMessage = () => {
    if (warningLevel === 'exceeded') {
      return t('upgrade.warnings.limitReached', { resourceType, ns: 'pricing' }) as string
    } else if (warningLevel === 'critical') {
      return t('upgrade.warnings.onlyRemaining', { remaining, resourceType, ns: 'pricing' }) as string
    } else if (warningLevel === 'warning') {
      return t('upgrade.warnings.percentageUsed', { percentage: Math.round(percentage), resourceType, ns: 'pricing' }) as string
    }
    return t('upgrade.warnings.usage', { current, limit, resourceType, ns: 'pricing' }) as string
  }

  if (warningLevel === 'info') return null

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${className}`}>
      <div className="flex items-center gap-3">
        {warningLevel === 'exceeded' ? (
          <XCircle className="h-4 w-4 text-red-500" />
        ) : (
          <TrendingUp className="h-4 w-4 text-orange-500" />
        )}
        <div>
          <div className="text-sm font-medium">{getMessage()}</div>
          <div className="text-xs text-muted-foreground">
            {warningLevel === 'exceeded' ? (t('warnings.upgradeToContinue', { ns: 'pricing' }) as string) : (t('warnings.considerUpgrading', { ns: 'pricing' }) as string)}
          </div>
        </div>
      </div>
      <Button 
        size="sm" 
        variant={getVariant()} 
        onClick={() => {
          // Track usage limit warning upgrade click
          trackUpgradeClick({
            source: 'usage_display',
            current_tier: 'free', // Assuming free tier for limit warnings
            target_tier: 'pro',
            upgrade_context: 'api_rate_limit',
            element_type: 'button',
            trigger_reason: `${warningLevel}_usage_warning`,
            additional_context: {
              resource_type: resourceType,
              current_usage: current,
              usage_limit: limit,
              usage_percentage: (current / limit) * 100
            }
          });
          
          onUpgrade();
        }}
      >
        {t('actions.upgrade', { ns: 'pricing' }) as string}
      </Button>
    </div>
  )
} 